import React, { FC } from 'react';
import styled from 'styled-components';
import { Select, Checkbox } from 'antd';
import { IBcfFieldMapping } from 'Data/BcfImport.api';

interface IMappingTableProps {
  fieldType: string;
  fieldLabel: string;
  fieldDescription: string;
  mapping: IBcfFieldMapping;
  availableValues: Array<{ id: string | number; name: string }>;
  onMappingChange: (bcfValue: string, coordlyValue: string | number) => void;
  isIgnored?: boolean;
  canBeIgnored?: boolean;
  required?: boolean;
  onIgnoreChange?: (isIgnored: boolean) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const RequiredBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #ef4444;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 1px 6px;
`;

const OptionalBadge = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  border-radius: 4px;
  padding: 1px 6px;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceOutline};
  width: 100%;
`;

const MappingGridHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: center;
  padding: 0 12px;
  margin-bottom: 8px;
`;

const ColumnLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const MappingGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const MappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.surface.surface};
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

const BcfValueCell = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
`;

const ArrowIcon = styled.div`
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  font-size: 14px;
  text-align: center;
  width: 16px;
`;

const SelectWrapper = styled.div`
  width: 100%;
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  z-index: 1;
`;

const DisabledText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  font-style: italic;
`;

const MappingGridContainer = styled.div`
  position: relative;
`;

const IgnoreCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const IgnoreCheckboxLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

const MappingTable: FC<IMappingTableProps> = ({
  fieldType,
  fieldLabel,
  fieldDescription,
  mapping,
  availableValues,
  onMappingChange,
  isIgnored = false,
  canBeIgnored = false,
  required = false,
  onIgnoreChange,
}) => {
  return (
    <Container>
      <Header>
        <TitleRow>
          <Title>{fieldLabel}</Title>
          {required ? (
            <RequiredBadge>Obrigatório</RequiredBadge>
          ) : (
            <OptionalBadge>Opcional</OptionalBadge>
          )}
        </TitleRow>
        <Description>{fieldDescription}</Description>
        {canBeIgnored && onIgnoreChange && (
          <IgnoreCheckboxRow>
            <Checkbox
              checked={isIgnored}
              onChange={(e) => onIgnoreChange(e.target.checked)}
            />
            <IgnoreCheckboxLabel>
              Ignorar {fieldLabel.toLowerCase()}
            </IgnoreCheckboxLabel>
          </IgnoreCheckboxRow>
        )}
      </Header>
      <Divider />

      <MappingGridHeader>
        <ColumnLabel>Valor do BCF</ColumnLabel>
        <div />
        <ColumnLabel>Valor da Coordly</ColumnLabel>
      </MappingGridHeader>

      <MappingGridContainer>
        {isIgnored && (
          <DisabledOverlay>
            <DisabledText>Campo ignorado</DisabledText>
          </DisabledOverlay>
        )}
        <MappingGrid>
          {mapping.bcfValues.map((bcfValue: string) => (
            <MappingRow key={bcfValue}>
              <BcfValueCell>{bcfValue}</BcfValueCell>
              <ArrowIcon>→</ArrowIcon>
              <SelectWrapper>
                <Select
                  placeholder="Selecione um valor"
                  value={mapping.mappingRules[bcfValue] || undefined}
                  onChange={(value) => onMappingChange(bcfValue, value)}
                  style={{ width: '100%' }}
                  disabled={isIgnored}
                  options={availableValues.map((val: any) => {
                    // Handle different ID field names from .NET API (PascalCase)
                    const id = val.TopicStatusId || val.TopicTypeId || val.TopicStageId ||
                      val.TopicPriorityId || val.ApplicationUserId ||
                      val.ConstructionSiteDisciplineId || val.Id;
                    const name = val.Name || val.FullName || val.Email;
                    return {
                      label: name,
                      value: id,
                    };
                  })}
                />
              </SelectWrapper>
            </MappingRow>
          ))}
        </MappingGrid>
      </MappingGridContainer>
    </Container>
  );
};

export default MappingTable;
